import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { roleColor } from '@beckersign/shared';

export interface Role { roleId: string; name: string }

export const MAX_ROLES = 6;

/** Appends a "Signer N" role to the envelope and returns it. */
export async function addRole(envelopeId: string, roles: Role[]): Promise<Role> {
  const role = {
    roleId: crypto.randomUUID().replace(/-/g, '').slice(0, 12),
    name: `Signer ${roles.length + 1}`,
  };
  await updateDoc(doc(db, 'envelopes', envelopeId), { roles: [...roles, role] });
  return role;
}

/**
 * Signing roles, not people.
 *
 * Fields are assigned to a ROLE ("Landlord", "Contractor"), and M5 binds actual
 * recipients to those roles at send time. This mirrors how templates work — a
 * template stores roles and routing, never specific people — so an envelope
 * built here can become a template without restructuring anything.
 */
export default function RolesEditor({
  envelopeId, roles, readOnly,
}: { envelopeId: string; roles: Role[]; readOnly: boolean }) {
  const [busy, setBusy] = useState(false);

  async function save(next: Role[]) {
    setBusy(true);
    try { await updateDoc(doc(db, 'envelopes', envelopeId), { roles: next }); }
    finally { setBusy(false); }
  }

  async function add() {
    setBusy(true);
    try { await addRole(envelopeId, roles); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <h2>Signing roles</h2>
      <p className="muted">
        Fields are assigned to a role. You’ll attach actual people to these roles when you send.
      </p>
      {roles.map((r, i) => (
        <div key={r.roleId} className="row" style={{ gap: '.5rem', marginBottom: '.4rem' }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: roleColor(i), flexShrink: 0 }} />
          <input
            value={r.name} maxLength={60} disabled={readOnly || busy}
            onChange={(e) => save(roles.map((x) => x.roleId === r.roleId ? { ...x, name: e.target.value } : x))}
            style={{ flex: 1, padding: '.4rem', minHeight: 38 }}
          />
          <button
            onClick={() => save(roles.filter((x) => x.roleId !== r.roleId))}
            disabled={readOnly || busy || roles.length <= 1}
            title={roles.length <= 1 ? 'An envelope needs at least one role' : 'Remove'}
          >
            Remove
          </button>
        </div>
      ))}
      <button onClick={add} disabled={readOnly || busy || roles.length >= MAX_ROLES} style={{ marginTop: '.4rem' }}>
        Add role
      </button>
    </div>
  );
}
